import {
	ActionIcon,
	Alert,
	Button,
	Card,
	Code,
	CopyButton,
	Flex,
	Group,
	Modal,
	Stack,
	Text,
	Tooltip,
} from '@mantine/core'
import { IconAlertCircle, IconCheck, IconCopy, IconRefresh, IconUserX } from '@tabler/icons-react'
import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
	CourseAiKickResult,
	CourseAiLearnerRow,
	CourseAiStaffJoinCodes,
	StaffJoinCodeKind,
	getCourseAiStaffJoinCodes,
	kickCourseAiStaffLearner,
	regenerateCourseAiStaffJoinCode,
	unwrapObject,
} from 'src/apis/courseAiAPI'
import { lockoutIncomplete } from './learnerDisplay'

/**
 * UD-627 (the product owner, 2026-10-14), both parts of Manage Learners:
 *
 * - the "Staff codes" box: the Tutor code and the UniVini Tester code, each with
 *   its own "Change code". Changing one never changes the other.
 * - Kick: on the row of a person who joined with one of the two codes.
 *
 * A code is shown here and copied from here, and goes nowhere else: it is in no
 * address, no toast and no error text.
 */

export const STAFF_CODE_KINDS: StaffJoinCodeKind[] = ['tutor', 'purchased']

export const STAFF_CODE_LABELS: Record<StaffJoinCodeKind, string> = {
	tutor: 'Tutor code',
	purchased: 'UniVini Tester code',
}

/** What a person who enters that code gets. */
export const STAFF_CODE_HINTS: Record<StaffJoinCodeKind, string> = {
	tutor: 'Joins the classes as a tutor',
	purchased: 'Studies like a learner, without paying',
}

/** The code a Manage Learners row was joined with, as the server names it on the row. */
export const CODE_ENTRY_LABELS: Record<string, string> = {
	TUTOR: STAFF_CODE_LABELS.tutor,
	PURCHASED: STAFF_CODE_LABELS.purchased,
}

/** Kick is offered on a row only when the server says the row itself is a staff code join. */
export const canKick = (row?: Pick<CourseAiLearnerRow, 'code_entry'> | null) =>
	!!row?.code_entry && !!CODE_ENTRY_LABELS[row.code_entry]

export const KICK_REENTRY_NOTE =
	'They can join again with the current code. Change the code first to keep them out.'

const codeOf = (codes: CourseAiStaffJoinCodes | null, kind: StaffJoinCodeKind) =>
	(kind === 'tutor' ? codes?.tutor_code : codes?.purchased_code) || ''

const errorMessage = (error: any, fallback: string) =>
	error?.response?.data?.message || error?.response?.data?.results?.message || fallback

function ServerRefusal({ message }: { message: string | null }) {
	if (!message) return null
	return (
		<Alert color="red" icon={<IconAlertCircle size={16} />}>
			{message}
		</Alert>
	)
}

/** "Change code": says what the change does, then replaces that one code. */
function ChangeCodeDialog({
	kind,
	onClose,
	onChanged,
}: {
	kind: StaffJoinCodeKind | null
	onClose: () => void
	onChanged: (codes: CourseAiStaffJoinCodes) => void
}) {
	const [saving, setSaving] = useState(false)
	const [refusal, setRefusal] = useState<string | null>(null)

	useEffect(() => {
		setRefusal(null)
	}, [kind])

	if (!kind) return null
	const label = STAFF_CODE_LABELS[kind]
	const other = STAFF_CODE_LABELS[kind === 'tutor' ? 'purchased' : 'tutor']

	const submit = async () => {
		setSaving(true)
		setRefusal(null)
		try {
			const codes = unwrapObject<CourseAiStaffJoinCodes>(await regenerateCourseAiStaffJoinCode(kind))
			if (!codes?.tutor_code || !codes?.purchased_code) throw new Error('no codes in the answer')
			onChanged(codes)
			toast.success(`${label} changed. The new code is in the Staff codes box.`)
		} catch (error) {
			setRefusal(errorMessage(error, 'Could not change the code'))
		} finally {
			setSaving(false)
		}
	}

	return (
		<Modal opened onClose={onClose} title={`Change the ${label}?`} size="md">
			<Stack spacing="sm">
				<Text size="sm">
					After the change the old code no longer works for new people. People who already joined
					keep learning as before.
				</Text>
				{kind === 'purchased' ? (
					<Text size="sm">
						A tester whose course has gone inactive needs the new code to resume it.
					</Text>
				) : null}
				<Text size="xs" color="dimmed">
					The {other} does not change. This cannot be undone: the old code is gone.
				</Text>
				<ServerRefusal message={refusal} />
				<Group position="right">
					<Button variant="default" onClick={onClose} disabled={saving}>
						Cancel
					</Button>
					<Button color="red" onClick={submit} loading={saving}>
						Change code
					</Button>
				</Group>
			</Stack>
		</Modal>
	)
}

/** The "Staff codes" box at the top of the Learners list. */
export function StaffCodesBox() {
	const [codes, setCodes] = useState<CourseAiStaffJoinCodes | null>(null)
	const [loading, setLoading] = useState(true)
	const [failed, setFailed] = useState(false)
	const [changing, setChanging] = useState<StaffJoinCodeKind | null>(null)

	const load = useCallback(async () => {
		setLoading(true)
		setFailed(false)
		try {
			const loaded = unwrapObject<CourseAiStaffJoinCodes>(await getCourseAiStaffJoinCodes())
			if (!loaded?.tutor_code || !loaded?.purchased_code) throw new Error('no codes in the answer')
			setCodes(loaded)
		} catch (error) {
			setCodes(null)
			setFailed(true)
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		load()
	}, [load])

	return (
		<Card withBorder radius="md" padding="sm" data-testid="staff-codes-box" sx={{ alignSelf: 'flex-start', maxWidth: '100%' }}>
			<Flex gap={28} wrap="wrap" align="center">
				<Stack spacing={0} sx={{ maxWidth: 230 }}>
					<Text fw={700}>Staff codes</Text>
					<Text size="xs" color="dimmed">
						A person types one of these in the app, under &quot;Enter course code&quot;.
					</Text>
				</Stack>
				{failed ? (
					<Group spacing={8}>
						<Text size="sm" color="red">
							Could not load the codes.
						</Text>
						<Button size="xs" variant="default" leftIcon={<IconRefresh size={14} />} onClick={load}>
							Try again
						</Button>
					</Group>
				) : (
					STAFF_CODE_KINDS.map((kind) => {
						const label = STAFF_CODE_LABELS[kind]
						const code = codeOf(codes, kind)
						return (
							<Stack key={kind} spacing={2} data-testid={`staff-code-${kind}`}>
								<Text size="xs" tt="uppercase" fw={700} color="dimmed">
									{label}
								</Text>
								<Group spacing={6} noWrap>
									<Code fz="lg" fw={700} sx={{ letterSpacing: 1, padding: '2px 10px', minWidth: 118, textAlign: 'center' }}>
										{loading || !code ? '........' : code}
									</Code>
									<CopyButton value={code} timeout={1500}>
										{({ copied, copy }) => (
											<Tooltip label={copied ? 'Copied' : 'Copy'} withinPortal>
												<ActionIcon
													variant="light"
													color={copied ? 'green' : 'gray'}
													onClick={copy}
													disabled={!code}
													aria-label={`Copy the ${label}`}
												>
													{copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
												</ActionIcon>
											</Tooltip>
										)}
									</CopyButton>
									<Button
										size="xs"
										variant="outline"
										color="red"
										disabled={!code}
										onClick={() => setChanging(kind)}
										aria-label={`Change the ${label}`}
									>
										Change code
									</Button>
								</Group>
								<Text size="xs" color="dimmed">
									{STAFF_CODE_HINTS[kind]}
								</Text>
							</Stack>
						)
					})
				)}
			</Flex>

			<ChangeCodeDialog
				kind={changing}
				onClose={() => setChanging(null)}
				onChanged={(changed) => {
					setCodes(changed)
					setChanging(null)
				}}
			/>
		</Card>
	)
}

const courseNames = (result?: CourseAiKickResult | null) =>
	(result?.courses || []).map((course) => course.course_name || 'a course').join(', ')

const kickIncomplete = (result?: CourseAiKickResult | null) =>
	(result?.courses || []).some((course) => lockoutIncomplete(course.effects))

/**
 * "Kick" on a staff code row. The person is out of the course once the server
 * has answered. If it says a step after that did not finish (the class chat,
 * the counts), Kick is sent once more, which only runs those steps again, and
 * staff are told when it still has not finished.
 */
export function KickDialog({
	learner,
	onClose,
	onDone,
}: {
	learner: CourseAiLearnerRow | null
	onClose: () => void
	onDone: () => void
}) {
	const [saving, setSaving] = useState(false)
	const [refusal, setRefusal] = useState<string | null>(null)

	useEffect(() => {
		setRefusal(null)
	}, [learner])

	if (!learner) return null
	const name = learner.name || 'This person'
	const codeLabel = CODE_ENTRY_LABELS[learner.code_entry || ''] || 'staff code'

	const submit = async () => {
		setSaving(true)
		setRefusal(null)
		try {
			const kicked = unwrapObject<CourseAiKickResult>(await kickCourseAiStaffLearner(learner.id))
			let last = kicked
			if (kickIncomplete(kicked)) {
				try {
					last = unwrapObject<CourseAiKickResult>(await kickCourseAiStaffLearner(learner.id))
				} catch (error) {
					// Reported below: the person is out of the course already.
				}
			}
			const lost = courseNames(kicked)
			if (kickIncomplete(last)) {
				toast.warning(`${name} was kicked, but may still be in a class chat. Please tell the developer.`)
			} else if (kicked?.not_kicked_course_ids?.length) {
				toast.warning(
					`${name} is out of ${lost}. One course could not be done: press Kick on the row that is left.`,
				)
			} else {
				toast.success(lost ? `${name} was kicked and is out of ${lost}.` : `${name} was kicked.`)
			}
			onDone()
		} catch (error) {
			setRefusal(errorMessage(error, 'Could not kick this person'))
		} finally {
			setSaving(false)
		}
	}

	return (
		<Modal opened onClose={onClose} title="Kick this person?" size="md">
			<Stack spacing="sm">
				<Stack spacing={2}>
					<Text size="sm">
						<b>{learner.name || '-'}</b> {learner.phone ? `(${learner.phone})` : ''}
					</Text>
					<Text size="sm">Course: {learner.course_name || '-'}</Text>
					<Text size="sm">Joined with the {codeLabel}</Text>
				</Stack>
				<Text size="sm">
					They lose the class at once: this course and the other courses the code opened, the class
					chats, the lists and the leaderboard. What they submitted is kept.
				</Text>
				<Text size="sm" fw={600}>
					{KICK_REENTRY_NOTE}
				</Text>
				<ServerRefusal message={refusal} />
				<Group position="right">
					<Button variant="default" onClick={onClose} disabled={saving}>
						Cancel
					</Button>
					<Button color="red" leftIcon={<IconUserX size={16} />} onClick={submit} loading={saving}>
						Kick
					</Button>
				</Group>
			</Stack>
		</Modal>
	)
}
